import aa, { InsightsMethodMap } from 'search-insights';
import { v4 as uuid } from 'uuid';
import cookie from 'js-cookie';
import { useAlgoliaAnalyticsState } from './useAlgoliaAnalyticsState';
import { useCallback } from 'react';

const ANONYMOUS_COOKIE_NAME = 'algolia-anonymous';
const COOKIE_NAME = 'algolia';

type ClickEventArgs = {
    objectId: string | string[];
    queryID: string;
    positions: number;
};

export const useAlgoliaAnalytics = () => {
    const { indexName, initialized, setInitialized, setIndexName } = useAlgoliaAnalyticsState();

    const initializeAnonymousUserToken = useCallback(() => {
        aa('getUserToken', null, (err, existingToken) => {
            if (err) {
                console.error(err);
                return;
            }

            const cookieToken = cookie.get(ANONYMOUS_COOKIE_NAME);
            const token = existingToken || cookieToken || uuid();

            if (!cookieToken) {
                cookie.set(ANONYMOUS_COOKIE_NAME, token, { expires: 60 });
            }
            aa('setUserToken', token);
        });
    }, []);

    const initializeUserToken = useCallback((userToken: string) => {
        const cookieToken = cookie.get(COOKIE_NAME);
        const token = cookieToken === userToken ? cookieToken : userToken;

        if (token) {
            cookie.set(COOKIE_NAME, token, { expires: 60 });
        }
        aa('setUserToken', token);
    }, []);

    /**
     * Analytics should only be instantiated if the user has accepted tracking
     */
    const initialize = useCallback(
        (appId: string, apiToken: string, indexName: string) => {
            aa('init', {
                appId: appId,
                apiKey: apiToken,
            });
            setIndexName(indexName);
            initializeAnonymousUserToken();
            setInitialized(true);
        },
        [initializeAnonymousUserToken, setIndexName, setInitialized]
    );

    const track = <MethodName extends keyof InsightsMethodMap>(
        method: MethodName,
        ...args: InsightsMethodMap[MethodName]
    ) => {
        if (!initialized || !indexName) {
            return;
        }

        aa('getUserToken', null, (err, existingToken) => {
            if (err) {
                aa(method, ...args);
            } else {
                args[0].userToken = existingToken;
                aa(method, ...args);
            }
        });
    };

    // Click events
    const trackSelectedCategory = (categoryFilter: string) => {
        track<'clickedObjectIDs'>('clickedObjectIDs', {
            index: indexName,
            eventName: 'Selected Category',
            objectIDs: [categoryFilter],
        });
    };

    // View events
    const trackViewedFilters = (filters: string[]) => {
        track<'viewedFilters'>('viewedFilters', {
            index: indexName,
            eventName: 'Viewed Filters',
            filters: filters,
        });
    };

    const trackViewedProduct = (objectId: string | string[]) => {
        track<'clickedObjectIDs'>('clickedObjectIDs', {
            index: indexName,
            eventName: 'Selected Category',
            objectIDs: Array.isArray(objectId) ? objectId : [objectId],
        });
    };

    const trackProductClick = ({ objectId, positions, queryID }: ClickEventArgs) => {
        track<'clickedObjectIDsAfterSearch'>('clickedObjectIDsAfterSearch', {
            index: indexName,
            eventName: 'Selected Category',
            objectIDs: Array.isArray(objectId) ? objectId : [objectId],
            queryID,
            positions: [positions],
        });
    };

    // Conversion events
    const trackConvertedFilters = (filters: string[]) => {
        track<'convertedFilters'>('convertedFilters', {
            index: indexName,
            eventName: 'Selected Filters',
            filters: filters,
        });
    };

    const trackAddToBasket = (objectIDs: string[], queryID: string) => {
        track<'convertedObjectIDsAfterSearch'>('convertedObjectIDsAfterSearch', {
            index: indexName,
            eventName: 'Product Added to Basket',
            objectIDs: objectIDs,
            queryID: queryID,
        });
    };

    return {
        initialize,
        initialized,
        initializeUserToken,
        trackConvertedFilters,
        trackViewedFilters,
        trackViewedProduct,
        trackProductClick,
        trackSelectedCategory,
        trackAddToBasket,
    };
};
